import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React, { useEffect, createContext } from 'react';
import { SubmissionPreferences } from './SubmissionPreferences/SubmissionPreferences';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { orderPreferenceActions, orderPreferenceReducer, orderPreferenceSliceKey } from './store/OrderPreference.redux';
import { orderPreferenceSaga } from './store/OrderPreference.saga';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useOrderPreferenceAlerts } from './OrderPreferenceAlerts';
import { isNotInteger } from 'app/helpers/objectHelpers';
import { notificationDataActions, notificationDataKey } from 'store/redux-store/notification/notification.redux';
import { notificationSaga } from 'store/redux-store/notification/notification.saga';
import { initialSubmissionPrefState } from './store/utils';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';
import { TemplateUserTypes } from '@AMIEWEB/Common/SubmissionPreference/Common/helper';
import { TemplateCategory } from '@AMIEWEB/Common/SubmissionPreference/SubmissionEmail/AddTemplateModal';
import { useDecision } from '@optimizely/react-sdk';
import { ff_order_global_margintool, ff_order_global_margintool_ui } from 'app/constants/FeatureFlags/MarginTool/Keys';
import { selectUser } from 'oidc/user.selectors';

export const AddOnContext = createContext<{ orderId: number }>({ orderId: 0 });

export const OrderPreferencesWrapper = () => {
  useInjectReducer({ key: orderPreferenceSliceKey, reducer: orderPreferenceReducer });
  useInjectSaga({ key: orderPreferenceSliceKey, saga: orderPreferenceSaga });
  useInjectSaga({ key: notificationDataKey, saga: notificationSaga });

  const params = useParams<{ id: string }>();
  const selectOrderUiDetails = useSelector(selectOrderDetailsUIView);
  const [featureFlagOrderMarginTool] = useDecision(ff_order_global_margintool);
  const [featureFlagOrderMarginToolUI] = useDecision(ff_order_global_margintool_ui);

  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  useOrderPreferenceAlerts();

  useEffect(() => {
    const orderId = parseInt(params.id);
    dispatch(orderPreferenceActions.getActivePlacementCounts({ orderId }));
    dispatch(notificationDataActions.getTemplatesByCategory({ category: TemplateCategory.PacketSubmission }));
    if (selectOrderUiDetails?.facilityId) {
      dispatch(
        notificationDataActions.getFacilityTemplatesByCategory({
          category: TemplateCategory.PacketSubmission,
          userType: TemplateUserTypes.Facility,
          userId: selectOrderUiDetails?.facilityId,
        }),
      );
    }
    if (featureFlagOrderMarginTool.enabled && featureFlagOrderMarginToolUI.enabled) {
      dispatch(
        orderDataActions.getOrderTargetMargins({ payload: { orderId: orderId, userId: user?.userInfo?.employeeId } }),
      );
    }
    dispatch(notificationDataActions.getFacilityBaseTemplate({ userType: TemplateUserTypes.Facility }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const orderId = parseInt(params.id);
    if (!isNotInteger(orderId)) {
      dispatch(orderPreferenceActions.getAutomationStatusByStage({ orderId }));
    }

    return () => {
      dispatch(orderPreferenceActions.setAutomationPreferenceData(initialSubmissionPrefState));
      dispatch(orderDataActions.setConfirmationDialog({ open: false, tab: 0 }));
      dispatch(orderDataActions.setIsPreferenceChaged(false));
      dispatch(orderDataActions.resetTargetMarginsMetaData());
    };
  }, [dispatch, params.id]);

  return (
    <AddOnContext.Provider value={{ orderId: parseInt(params.id) }}>
      <LayoutGrid direction="column">
        <LayoutGridItem>
          <SubmissionPreferences />
        </LayoutGridItem>
      </LayoutGrid>
    </AddOnContext.Provider>
  );
};
